import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import styled from "styled-components";

import {
  AuthWrapper,
  FormWrapper,
  ContentLogin,
  ImageWrapper,
  Form,
  ConfirmWrapp,
  LinkWrap,
  EmptyColumn,
  ErrorMessage,
} from "../../style/Auth";

import logo from "../../images/ic_logo.svg";
import emptyState from "../../images/contactLogo.svg";
import Button from "../../components/button/index";
import PlayerLoader from "../../components/PlayerLoader";

import { searchPlayerForOnboarding, mapSelfToPlayer } from "./api";
import { getMediaUrl } from "../../helpers";
import { userActions } from "../../store/user/userSlice";
import { useLexicon } from "../../hooks/useLexicon";

const interp = (tmpl, nodes) => {
  const parts = String(tmpl).split(/\{[^}]+\}/);
  return parts.flatMap((p, i) => (i < nodes.length ? [p, nodes[i]] : [p]));
};

// /player/onboarding/pick-player — self-serve onboarding step that connects
// the freshly-paid auth_user to a platform_api_player record. Mirrors the
// team-admin "Invite a player" picker (debounced ILIKE search → click row →
// confirm) but writes the mapping directly. **One-shot only** — once a
// mapping exists, switching is admin-override territory; the page redirects
// already-mapped users straight to /player/dashboard.

const SEARCH_DEBOUNCE_MS = 250;
const MIN_QUERY_LEN = 2;

const PickPlayerPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const l = useLexicon();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Debounced search.
  useEffect(() => {
    if (selected) return undefined;
    if (!query || query.length < MIN_QUERY_LEN) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      searchPlayerForOnboarding(query)
        .then((res) => {
          if (cancelled) return;
          const body = res.data ?? {};
          const list = Array.isArray(body) ? body : (body.data ?? body.results ?? []);
          setResults((Array.isArray(list) ? list : []).slice(0, 12));
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, selected]);

  const canSubmit = useMemo(
    () => selected && !submitting,
    [selected, submitting]
  );

  const handleConfirm = async (event) => {
    event?.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await mapSelfToPlayer(selected.id);
      const playerId = res?.data?.mapping?.player_id ?? selected.id;
      // Mirror the mapping onto local auth context so guards relying on
      // `mapped_player_id` (NotMapped, the player layout) stop bouncing.
      localStorage.setItem("mapped_player_id", String(playerId));
      if (userActions.setAuthContext) {
        dispatch(
          userActions.setAuthContext({
            user_type: "player",
            roles: ["player"],
            mapped_player_id: playerId,
            organization_id: null,
            subscription: null,
          })
        );
      }
      navigate("/player/dashboard", { replace: true });
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === "ALREADY_MAPPED") {
        // Already mapped — redirect to dashboard rather than show a confusing
        // error. The user clicked here through stale state.
        navigate("/player/dashboard", { replace: true });
        return;
      }
      setError(
        err?.response?.data?.error || err?.message || l(1793)
      );
      setSubmitting(false);
    }
  };

  return (
    <AuthWrapper>
      <FormWrapper>
        <ContentLogin>
          <EmptyColumn />
          <Form onSubmit={handleConfirm}>
            <a href="/">
              <img className="logo" src={logo} alt="Logo" />
            </a>
            <h1>{l(1783)}</h1>
            <p>{interp(l(1784), [<strong key="x">{l(1785)}</strong>])}</p>

            {selected ? (
              <SelectedPlayer>
                <PlayerAvatar
                  src={
                    selected.photos?.[0]?.full
                      ? getMediaUrl(selected.photos[0].full)
                      : emptyState
                  }
                  alt=""
                />
                <PlayerInfo>
                  <PlayerName>
                    {selected.display_name ||
                      `${selected.name || ""} ${selected.surname || ""}`.trim()}
                  </PlayerName>
                  <PlayerMeta>
                    {selected.nationality?.[0]?.flag && (
                      <Flag
                        src={getMediaUrl(selected.nationality[0].flag)}
                        alt=""
                      />
                    )}
                    {selected.team?.name && <span>{selected.team.name}</span>}
                  </PlayerMeta>
                </PlayerInfo>
                <ClearButton
                  type="button"
                  onClick={() => {
                    setSelected(null);
                    setError(null);
                  }}
                >
                  {l(1786)}
                </ClearButton>
              </SelectedPlayer>
            ) : (
              <SearchBlock>
                <SearchInput
                  type="text"
                  placeholder={l(1787)}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                />
                {searching && <Hint>{l(1788)}</Hint>}
                {!searching && query.length >= MIN_QUERY_LEN && results.length === 0 && (
                  <Hint>{l(1789)}</Hint>
                )}
                {results.length > 0 && (
                  <Results>
                    {results.map((p) => (
                      <ResultItem
                        key={p.id}
                        type="button"
                        onClick={() => {
                          setSelected(p);
                          setResults([]);
                          setQuery("");
                        }}
                      >
                        <ResultAvatar
                          src={
                            p.photos?.[0]?.full
                              ? getMediaUrl(p.photos[0].full)
                              : emptyState
                          }
                          alt=""
                        />
                        <ResultBody>
                          <ResultName>
                            {p.display_name ||
                              `${p.name || ""} ${p.surname || ""}`.trim()}
                          </ResultName>
                          <ResultMeta>
                            {p.nationality?.[0]?.flag && (
                              <Flag
                                src={getMediaUrl(p.nationality[0].flag)}
                                alt=""
                              />
                            )}
                            {p.team?.name && <span>{p.team.name}</span>}
                          </ResultMeta>
                        </ResultBody>
                        <ResultChevron aria-hidden>›</ResultChevron>
                      </ResultItem>
                    ))}
                  </Results>
                )}
              </SearchBlock>
            )}

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <ConfirmWrapp>
              <Button
                title={submitting ? l(1790) : l(1791)}
                size="full"
                sizeResponse="full"
                type="submit"
                disabled={!canSubmit}
              />
            </ConfirmWrapp>
          </Form>
          <LinkWrap>
            <h3>
              {l(1792)}{" "}
              <a href="mailto:support@smrtstats.com">support@smrtstats.com</a>
            </h3>
          </LinkWrap>
        </ContentLogin>
      </FormWrapper>
      <ImageWrapper />
    </AuthWrapper>
  );
};

export default PickPlayerPage;

const SearchBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SearchInput = styled.input`
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${(p) => p.theme.lines};
  background: transparent;
  color: ${(p) => p.theme.white};
  font-size: 14px;
  &:focus { outline: none; border-color: ${(p) => p.theme.green}; }
  &::placeholder { color: ${(p) => p.theme.blueish2}; }
`;

const Hint = styled.div`
  padding: 8px 4px;
  font-size: 12px;
  color: ${(p) => p.theme.blueish2};
  font-family: "SatoshiRegular", sans-serif;
`;

const Results = styled.div`
  border: 1px solid rgba(200, 241, 53, 0.12);
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.22);
  max-height: 280px;
  overflow-y: auto;
  margin-top: 6px;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ResultItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 9px 10px;
  border-radius: 10px;
  border: 1px solid transparent;
  background: transparent;
  color: ${(p) => p.theme.white};
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(200, 241, 53, 0.06);
    border-color: rgba(200, 241, 53, 0.16);
    transform: translateX(3px);
  }

  &:hover > span[aria-hidden] {
    opacity: 1;
    transform: translateX(0);
  }
`;

const ResultAvatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  object-fit: cover;
  background: rgba(200, 241, 53, 0.06);
  flex-shrink: 0;
`;

const ResultBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const ResultName = styled.div`
  color: ${(p) => p.theme.white};
  font-family: "SatoshiMedium", sans-serif;
  font-size: 14px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ResultMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${(p) => p.theme.blueish2};
  font-size: 12px;
  min-width: 0;
  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Flag = styled.img`
  width: 16px;
  height: 12px;
  object-fit: cover;
  border-radius: 2px;
  flex-shrink: 0;
`;

const ResultChevron = styled.span`
  color: ${(p) => p.theme.green};
  font-size: 20px;
  line-height: 1;
  margin-left: auto;
  opacity: 0;
  transform: translateX(-5px);
  transition: all 0.2s ease;
`;

const SelectedPlayer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid ${(p) => p.theme.green};
  border-radius: 10px;
  background: rgba(200, 255, 0, 0.05);
`;

const PlayerAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  background: rgba(200, 241, 53, 0.06);
  flex-shrink: 0;
`;

const PlayerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const PlayerName = styled.div`
  color: ${(p) => p.theme.white};
  font-family: "SatoshiBold", sans-serif;
  font-size: 14px;
`;

const PlayerMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${(p) => p.theme.blueish2};
  font-size: 12px;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: ${(p) => p.theme.green};
  cursor: pointer;
  font-size: 13px;
  text-decoration: underline;
  flex-shrink: 0;
`;
